import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { DropdownMenu, Icon, Tooltip, Typography } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { Client } from "@/redux/slices/client/types";
import { ClientsTab } from "./tabs";
import { ArchivedStatuses, ClientsTableData } from "./types";

const CLIENT_COLUMNS: Record<string, DataTableColumnDef<ClientsTableData>> = {
  name: { accessorKey: "name", header: "Name", cell: (props) => props.row.original.name, id: "name", size: 250 },
  emailMobile: { accessorKey: "emailMobile", header: "Email / Mobile", cell: (props) => props.row.original.emailMobile, id: "email" },
  company: { accessorKey: "company", header: "Company", cell: (props) => props.row.original.company, size: 200, enableSorting: false },
  accountType: {
    accessorKey: "accountType",
    header: "Account Type",
    cell: (props) => props.row.original.accountType,
    enableSorting: false,
    size: 100,
  },
  hearAboutUs: {
    accessorKey: "hearAboutUs",
    header: "Hear about us",
    cell: (props) => props.row.original.hearAboutUs,
    enableSorting: false,
    size: 300,
  },
  joined: { accessorKey: "joined", header: "Joined", cell: (props) => props.row.original.joined, enableSorting: false, size: 100 },
  paymentMethod: {
    accessorKey: "paymentMethod",
    header: "Payment Method",
    cell: (props) => props.row.original.paymentMethod,
    enableSorting: false,
    size: 130,
  },
  madeBooking: {
    accessorKey: "madeBooking",
    header: "Made Booking",
    cell: (props) => props.row.original.madeBooking,
    enableSorting: false,
    size: 110,
  },
  status: { accessorKey: "status", header: "Status", cell: (props) => props.row.original.status, enableSorting: false, size: 100 },
  action: { accessorKey: "action", header: "Action", cell: (props) => props.row.original.action, size: 75, enableSorting: false },
};

/**
 * Pending invites have no name/company/booking history yet, so the Pending tab shows a
 * trimmed Email / Account Type / Status / Action layout (Figma 11233-70796). My Clients
 * and Archived use the full set.
 */
export const generateClientColumns = (tab: ClientsTab): DataTableColumnDef<ClientsTableData>[] => {
  const { name, emailMobile, company, accountType, hearAboutUs, joined, paymentMethod, madeBooking, status, action } = CLIENT_COLUMNS;

  // Pending has only 4 columns in a full-width table, so give Email/Mobile and Status
  // matching flex widths (Figma 11233-70796) — otherwise Email/Mobile eats all the surplus
  // and Account Type / Status / Action bunch up on the right.
  if (tab === "pending") {
    return [
      { ...emailMobile, size: 380 },
      { ...accountType, size: 150 },
      { ...status, size: 380 },
      { ...action, size: 90 },
    ];
  }

  return [name, emailMobile, company, accountType, hearAboutUs, joined, paymentMethod, madeBooking, status, action];
};

export type ClientRowHandlers = {
  onRemoveAccess: (client: Client) => void;
  onReactivate: (client: Client) => void;
  onResendInvite: (client: Client) => void;
  onCancelInvite: (client: Client) => void;
  /** Start an OBO booking for this client — resets any in-flight draft before routing to the sheet. */
  onBookTrip: (client: Client) => void;
};

export const generateClientData = (items: Client[], dateFormat: string, operatorId: string, handlers: ClientRowHandlers) => {
  const data = items.map(
    (i) =>
      ({
        name: (
          <span className="flex items-center gap-1.5">
            {
              // Active and removed/inactive clients both have a detail page (removed = view-only
              // with the banner), so both are clickable. Pending invites have no detail yet.
              i.status === "active" || ArchivedStatuses.includes(i.status) ? (
                <Tooltip content={i.status === "active" ? "Go to Manage Account" : "View account"} placement="bottom">
                  <Link to={getManageClientPath(operatorId, i.id)} className="hover:text-info">
                    <Typography variant="paragraph">{i.name}</Typography>
                  </Link>
                </Tooltip>
              ) : (
                <Typography variant="paragraph">{i.name}</Typography>
              )
            }
            {i.isVip && <Icon name="diamond" isCustom size="md" aria-label="VIP" className="shrink-0" />}
          </span>
        ),
        emailMobile: (
          <div className="inline-block">
            {i.pendingEmail ? (
              <Typography variant="paragraph" className="break-all">
                <span className="text-warning">{i.pendingEmail}</span>
                <Tooltip content="The client will need to log in using this email to confirm the change." placement="right">
                  <Icon name="Danger" variant="Bold" size="md" className="ml-1 inline-block align-middle text-warning" />
                </Tooltip>
              </Typography>
            ) : (
              <Typography variant="paragraph">{i.email}</Typography>
            )}
            <Typography variant="small" className="text-neutral-dark-gray">
              {i.phone}
            </Typography>
          </div>
        ),
        company:
          i.companyName &&
          (i.status === "active" ? (
            <Tooltip content="View Company" placement="bottom">
              <Link to={`/companies/${i.companyId}`} className="hover:text-info" target="_blank">
                <Typography variant="paragraph">{i.companyName}</Typography>
              </Link>
            </Tooltip>
          ) : (
            <Typography variant="paragraph">{i.companyName}</Typography>
          )),
        accountType: i.accountType,
        hearAboutUs: i.hearAboutUs,
        joined: format(parseISO(i.joined), dateFormat),
        paymentMethod: i.paymentMethod || "-",
        madeBooking: renderMadeBooking(i.madeBooking),
        status: renderClientStatus(i.status),
        action: renderClientActions(i, operatorId, handlers),
      } as ClientsTableData)
  );

  return data;
};

const renderMadeBooking = (madeBooking: boolean) =>
  madeBooking ? (
    <Icon name="TickCircle" variant="Bold" size="md" className="text-success" aria-label="Has made a booking" />
  ) : (
    <Typography variant="paragraph" className="text-neutral-dark-gray" aria-label="No bookings">
      -
    </Typography>
  );

const renderClientStatus = (status: string) => {
  switch (status) {
    case "active":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-success" /> Active
        </Typography>
      );
    case "removed":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-danger" /> Inactive
        </Typography>
      );
    case "pending":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-neutral-silver-gray" /> Pending (Account Setup)
        </Typography>
      );
    default:
      return <></>;
  }
};

/** "Tyson Anderson" → "Tyson A." for the row-menu Book a Trip label (Figma 2325-6939). */
const getBookTripName = (name: string) => {
  const [first, last] = (name ?? "").trim().split(" ");
  return last ? `${first} ${last[0]}.` : first || "";
};

const renderClientActions = (client: Client, operatorId: string, handlers: ClientRowHandlers) => {
  const { onRemoveAccess, onReactivate, onResendInvite, onCancelInvite, onBookTrip } = handlers;

  return (
    <>
      <DropdownMenu button={<Icon name="options" isCustom size="lg" />} position="bottom-start" className="!flex max-w-[24px]">
        {client.status === "active" ? (
          <>
            <Link to={getManageClientPath(operatorId, client.id)}>
              <DropdownMenu.Item>
                <Icon name="ArrowRight" className="mr-2.5 text-neutral-black" size="sm" />
                <Typography className="text-neutral-black">Manage Account</Typography>
              </DropdownMenu.Item>
            </Link>
            {/* Opens the operator book-on-behalf sheet for this client (same route as the client-detail
                "Book a Trip" button). Only rendered for active clients — the branch guard above.
                onBookTrip resets any in-flight draft before routing (ClientsTable) so a previous attempt's
                locations never carry into this client's booking; it also tags the origin ("client-profile")
                so the confirmation's "Back" returns to this client's profile. */}
            <DropdownMenu.Item onClick={() => onBookTrip(client)}>
              <Icon name="ArchiveBook" className="mr-2.5 text-neutral-black" size="sm" />
              <Typography className="text-neutral-black">Book a Trip for {getBookTripName(client.name)}</Typography>
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => onRemoveAccess(client)}>
              <Icon name="Trash" className="mr-2.5 text-danger" size="sm" />
              <Typography className="text-neutral-black">Remove Access</Typography>
            </DropdownMenu.Item>
          </>
        ) : client.status === "pending" ? (
          <>
            <DropdownMenu.Item onClick={() => onResendInvite(client)}>
              <Icon name="Send2" className="mr-2.5 text-neutral-black" size="sm" />
              <Typography className="text-neutral-black">Resend Email Invitation</Typography>
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => onCancelInvite(client)}>
              <Icon name="close" isCustom className="mr-2.5 text-neutral-black" size="sm" />
              <Typography className="text-neutral-black">Cancel Email Invitation</Typography>
            </DropdownMenu.Item>
          </>
        ) : (
          <DropdownMenu.Item onClick={() => onReactivate(client)}>
            <Icon name="Send2" className="mr-2.5 text-neutral-black" size="sm" />
            <Typography className="text-neutral-black">Reactivate Client</Typography>
          </DropdownMenu.Item>
        )}
      </DropdownMenu>
    </>
  );
};

export const getManageAccountPath = (id: string) => {
  const phpHostUrl = getPhpHostUrl();
  const viewPath = `${phpHostUrl}/o/context/switch?redirection=/passenger/${id}/booking/history`;

  return viewPath;
};

/** In-app deep link to the operator Manage-Client record (DEV-1168). */
export const getManageClientPath = (operatorId: string, clientId: string) => `/operator/${operatorId}/clients/${clientId}/details`;
/** Operator book-on-behalf sheet for a client (DEV-462) — same route the client-detail "Book a Trip" button uses. */
export const getClientBookingPath = (operatorId: string, clientId: string) => `/operator/${operatorId}/clients/${clientId}/booking`;
